import React, { useState } from 'react';
import {
  Search,
  ExternalLink,
  Download,
  X,
  FileText,
  Image,
  Video,
  File,
} from 'lucide-react';

interface LibraryLink {
  id: string;
  title: string;
  source: string;
  sourceIcon: string;
  description: string;
  url: string;
}

interface LibraryFile {
  id: string;
  name: string;
  type: 'pdf' | 'image' | 'video' | 'other';
  size: string;
  uploadedAt: string;
}

interface TeacherLibraryTabProps {
  teacherId: string;
}

const mockLinks: LibraryLink[] = [
  {
    id: '1',
    title: 'World War 2',
    source: 'Wikipedia',
    sourceIcon: 'W',
    description: 'Lorem ipsum dolor sit amet Lorem ipsum dolor sit amet Lorem ipsum dolor sit amet Lorem ipsum dolor sit amet',
    url: 'https://wikipedia.org/wiki/World_War_II',
  },
  {
    id: '2',
    title: 'The French Revolution',
    source: 'Britannica',
    sourceIcon: 'B',
    description: 'A period of significant social and political upheaval in France from 1789 to 1799',
    url: 'https://britannica.com/event/French-Revolution',
  },
  {
    id: '3',
    title: 'The Solar System',
    source: 'National Geographic',
    sourceIcon: 'N',
    description: 'The gravitationally bound system comprising the Sun and the objects that orbit it',
    url: 'https://nationalgeographic.com/science/article/solar-system',
  },
  {
    id: '4',
    title: 'Artificial Intelligence',
    source: 'TED',
    sourceIcon: 'T',
    description: 'The simulation of human intelligence processes by machines, especially computer systems',
    url: 'https://ted.com/topics/ai',
  },
  {
    id: '5',
    title: 'The Renaissance',
    source: 'Oxford',
    sourceIcon: 'O',
    description: 'A cultural movement that profoundly affected European intellectual life in the early modern period',
    url: 'https://oxford.com/renaissance',
  },
  {
    id: '6',
    title: 'Cold War',
    source: 'History.com',
    sourceIcon: 'H',
    description: 'A period of geopolitical tension between the Soviet Union and the United States after World War II',
    url: 'https://history.com/topics/cold-war',
  },
  {
    id: '7',
    title: 'Climate Change',
    source: 'YouTube',
    sourceIcon: 'Y',
    description: 'A long-term change in the average weather patterns of the Earth, often attributed to human activity',
    url: 'https://youtube.com/climate-change',
  },
];

const mockFiles: LibraryFile[] = [
  { id: '1', name: 'Physics_Lecture_Notes.pdf', type: 'pdf', size: '2.4 MB', uploadedAt: '2024-01-15' },
  { id: '2', name: 'Chemistry_Diagrams.png', type: 'image', size: '1.2 MB', uploadedAt: '2024-01-10' },
  { id: '3', name: 'Biology_Video.mp4', type: 'video', size: '45 MB', uploadedAt: '2024-01-08' },
  { id: '4', name: 'Math_Formulas.pdf', type: 'pdf', size: '500 KB', uploadedAt: '2024-01-05' },
];

const sourceColors: Record<string, string> = {
  Wikipedia: 'bg-gray-100 text-gray-700',
  Britannica: 'bg-blue-100 text-blue-700',
  'National Geographic': 'bg-yellow-100 text-yellow-700',
  TED: 'bg-red-100 text-red-700',
  Oxford: 'bg-purple-100 text-purple-700',
  'History.com': 'bg-orange-100 text-orange-700',
  YouTube: 'bg-red-100 text-red-700',
};

export function TeacherLibraryTab({ teacherId }: TeacherLibraryTabProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [showLibraryModal, setShowLibraryModal] = useState(false);
  const [selectedLink, setSelectedLink] = useState<LibraryLink | null>(null);

  const filteredLinks = mockLinks.filter(
    (link) =>
      link.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      link.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleOpenLink = (link: LibraryLink) => {
    setSelectedLink(link);
    setShowLibraryModal(true);
  };

  const getFileIcon = (type: string) => {
    switch (type) {
      case 'pdf':
        return <FileText className="w-5 h-5 text-red-500" />;
      case 'image':
        return <Image className="w-5 h-5 text-blue-500" />;
      case 'video':
        return <Video className="w-5 h-5 text-purple-500" />;
      default:
        return <File className="w-5 h-5 text-gray-500" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Links Section */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Links</h2>
            <p className="text-sm text-gray-500">{mockLinks.length} resources</p>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search links..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
        </div>

        {/* Links List */}
        <div className="space-y-3">
          {filteredLinks.map((link) => (
            <div
              key={link.id}
              onClick={() => handleOpenLink(link)}
              className="flex items-start gap-4 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
            >
              {/* Source Icon */}
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold ${
                sourceColors[link.source] || 'bg-gray-100 text-gray-700'
              }`}>
                {link.sourceIcon}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs text-gray-500">{link.source}</span>
                </div>
                <h3 className="font-medium text-gray-900">{link.title}</h3>
                <p className="text-sm text-gray-500 line-clamp-2">{link.description}</p>
              </div>

              {/* Action */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(link.url, '_blank');
                }}
                className="p-2 text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg"
              >
                <ExternalLink className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>

        {filteredLinks.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No links found matching your search
          </div>
        )}
      </div>

      {/* Library Modal */}
      {showLibraryModal && selectedLink && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Library</h3>
              <button
                onClick={() => setShowLibraryModal(false)}
                className="p-1 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {/* Selected Link Info */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center font-bold text-lg ${
                    sourceColors[selectedLink.source] || 'bg-gray-100 text-gray-700'
                  }`}>
                    {selectedLink.sourceIcon}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-500">{selectedLink.source}</p>
                    <h4 className="font-semibold text-gray-900">{selectedLink.title}</h4>
                    <p className="text-sm text-gray-600 mt-1">{selectedLink.description}</p>
                  </div>
                </div>
              </div>

              {/* Files */}
              <h4 className="font-medium text-gray-900 mb-3">Related Files</h4>
              <div className="space-y-2">
                {mockFiles.map((file) => (
                  <div
                    key={file.id}
                    className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
                  >
                    <div className="flex items-center gap-3">
                      {getFileIcon(file.type)}
                      <div>
                        <p className="text-sm font-medium text-gray-900">{file.name}</p>
                        <p className="text-xs text-gray-500">{file.size}</p>
                      </div>
                    </div>
                    <button className="p-2 text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg">
                      <Download className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200">
              <button
                onClick={() => setShowLibraryModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Close
              </button>
              <button
                onClick={() => window.open(selectedLink.url, '_blank')}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center gap-2"
              >
                <ExternalLink className="w-4 h-4" />
                Open Link
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
